import {
  expectedIcDph,
  icDphMatchesDic,
  isValidDic,
  isValidIban,
  isValidIcDph,
  isValidIco,
  parseMoneyToCents,
  tt,
} from '@agos/shared'

/**
 * `/settings-billing` — the company's own identity on the invoices it issues, and the approval gate.
 *
 * **FB-135.** `approvalThresholdEur` is not a limit with a sensible zero. It is a **switch**:
 *
 * ```java
 * // InvoiceService.approvalThreshold()
 * "SELECT COALESCE((billing_settings ->> 'approvalThresholdEur')::numeric, 0) …"
 *   .filter(threshold -> threshold.signum() > 0)
 *   .orElse(BigDecimal.ZERO);
 * ```
 *
 * and the consumer treats zero as *nothing needs approving*:
 *
 * ```ts
 * export function needsApproval(invoice: InvoiceRow, thresholdCents: number): boolean {
 *   if (thresholdCents <= 0) return false
 *   …
 * ```
 *
 * On the restored data `billing_settings` holds `{dic: "", ico: "", iban: ""}` — the threshold has never
 * been set, so `GET /invoices` answers `threshold: 0` and **no supplier invoice ever requires a main
 * administrator's approval**, including the two over 500 €. Legacy renders it as a plain number field
 * defaulting to `0`, with nothing to say that the default disables a control on another page.
 *
 * **FB-136.** `ico`, `dic`, `icDph` and `iban` are the company's own identifiers, printed on every
 * outgoing invoice, and legacy validates **none** of them — while the *supplier's* equivalents are
 * checked on `/invoices` with helpers that already live in `@agos/shared`. The same four fields, one
 * screen apart, one side checked and the other not.
 *
 * **And the section is replaced, not merged.** `replaceSection("billing_settings", body)`, so a partial
 * save drops whatever it omits — including `logoUrl`, which `GET /settings/footer` puts on the customer
 * site unauthenticated. The endpoint refuses such a body now rather than performing it, so this page's
 * habit of sending all eleven keys is the contract rather than a courtesy. Blanking a field explicitly
 * still works; only forgetting one is refused.
 */

export interface BillingSettings {
  ico?: string
  dic?: string
  icDph?: string
  billingAddress?: string
  iban?: string
  invoiceDueDays?: number
  logoUrl?: string
  autoInvoice?: boolean
  sendInvoiceEmail?: boolean
  invoiceNote?: string
  approvalThresholdEur?: number | string
}

export interface BillingForm {
  ico: string
  dic: string
  icDph: string
  billingAddress: string
  iban: string
  invoiceDueDays: number
  logoUrl: string
  autoInvoice: boolean
  sendInvoiceEmail: boolean
  invoiceNote: string
  approvalThreshold: string
}

export const DUE_DAYS_MIN = 1
export const DUE_DAYS_MAX = 90
export const DEFAULT_DUE_DAYS = 14

export const EMPTY_BILLING: BillingForm = {
  ico: '',
  dic: '',
  icDph: '',
  billingAddress: '',
  iban: '',
  invoiceDueDays: DEFAULT_DUE_DAYS,
  logoUrl: '',
  autoInvoice: false,
  sendInvoiceEmail: true,
  invoiceNote: '',
  approvalThreshold: '',
}

export function formOf(settings: BillingSettings | undefined): BillingForm {
  const threshold = settings?.approvalThresholdEur
  return {
    ico: settings?.ico ?? '',
    dic: settings?.dic ?? '',
    icDph: settings?.icDph ?? '',
    billingAddress: settings?.billingAddress ?? '',
    iban: settings?.iban ?? '',
    invoiceDueDays: settings?.invoiceDueDays ?? DEFAULT_DUE_DAYS,
    logoUrl: settings?.logoUrl ?? '',
    autoInvoice: settings?.autoInvoice === true,
    sendInvoiceEmail: settings?.sendInvoiceEmail !== false,
    invoiceNote: settings?.invoiceNote ?? '',
    // Kept as text so *unset* and *zero* stay distinguishable — they mean the same thing to the
    // consumer, but only one of them is a decision somebody made.
    approvalThreshold:
      threshold === undefined || threshold === null || threshold === '' ? '' : String(threshold),
  }
}

/**
 * Every key the section holds, sent every time.
 *
 * `replaceSection` does not merge. A body missing `logoUrl` takes the logo off the customer site's
 * footer, and nothing on this page would say so.
 */
export function payloadOf(form: BillingForm): Required<Omit<BillingSettings, 'approvalThresholdEur'>> & {
  approvalThresholdEur: number
} {
  return {
    ico: form.ico.trim(),
    dic: form.dic.trim(),
    icDph: form.icDph.trim().toUpperCase(),
    billingAddress: form.billingAddress.trim(),
    iban: form.iban.trim(),
    invoiceDueDays: clampDueDays(form.invoiceDueDays),
    logoUrl: form.logoUrl.trim(),
    autoInvoice: form.autoInvoice,
    sendInvoiceEmail: form.sendInvoiceEmail,
    invoiceNote: form.invoiceNote.trim(),
    // The consumer reads it as a numeric; an empty field means *no gate*, which is zero.
    approvalThresholdEur: thresholdCents(form.approvalThreshold) / 100,
  }
}

export function clampDueDays(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_DUE_DAYS
  return Math.min(DUE_DAYS_MAX, Math.max(DUE_DAYS_MIN, Math.round(value)))
}

/** §14.7: a threshold is money, so it is held and compared in cents. */
export function thresholdCents(value: string): number {
  const text = value.trim()
  if (text === '') return 0
  return Math.max(0, parseMoneyToCents(text))
}

// ─── FB-135: the switch that looks like a limit ───────────────────────────────

export type ApprovalState = 'off' | 'on'

export function approvalState(form: BillingForm): ApprovalState {
  return thresholdCents(form.approvalThreshold) > 0 ? 'on' : 'off'
}

/**
 * What the current threshold means for the invoice list, in words.
 *
 * The figure is not decoration and its zero is not neutral: with no threshold, an invoice of any size can
 * be confirmed by anyone who may confirm invoices at all.
 */
export function approvalMessage(form: BillingForm): string {
  if (approvalState(form) === 'off') {
    return tt(
      'Nie je nastavená žiadna hranica, takže schválenie hlavným administrátorom nepotrebuje žiadna faktúra - ani tá najvyššia.',
    )
  }
  return tt('Faktúry nad túto sumu musí schváliť hlavný administrátor, inak sa nedajú potvrdiť.')
}

// ─── FB-136: the identifiers on our own invoices ──────────────────────────────

export type IdentityField = 'ico' | 'dic' | 'icDph' | 'iban'

export type IdentityProblem = 'shape' | 'ic-dph-mismatch' | null

/**
 * Whether an identifier is one.
 *
 * The same four checks `/invoices` runs on a *supplier's* details, run here on our own. An empty field
 * is not a problem — the section is partly filled on this data and a settings page should not refuse to
 * open — but a wrong one is, because it goes on every invoice we issue.
 */
export function identityProblem(field: IdentityField, value: string): IdentityProblem {
  const text = value.trim()
  if (text === '') return null
  if (field === 'ico') return isValidIco(text) ? null : 'shape'
  if (field === 'dic') return isValidDic(text) ? null : 'shape'
  if (field === 'iban') return isValidIban(text) ? null : 'shape'
  return isValidIcDph(text) ? null : 'shape'
}

/**
 * Whether the VAT number matches the tax number it is derived from.
 *
 * A Slovak `IČ DPH` is `SK` plus the ten-digit DIČ, so a mismatch is a typo in one of the two — and it is
 * the kind that a tax office notices rather than a customer.
 */
export function icDphProblem(form: BillingForm): IdentityProblem {
  const shape = identityProblem('icDph', form.icDph)
  if (shape !== null) return shape
  if (form.icDph.trim() === '' || form.dic.trim() === '') return null
  return icDphMatchesDic(form.icDph.trim(), form.dic.trim()) ? null : 'ic-dph-mismatch'
}

export function suggestedIcDph(form: BillingForm): string | null {
  if (form.dic.trim() === '' || !isValidDic(form.dic.trim())) return null
  const suggestion = expectedIcDph(form.dic.trim())
  return suggestion === form.icDph.trim().toUpperCase() ? null : suggestion
}

export function identityProblemMessage(field: IdentityField, problem: IdentityProblem): string {
  if (problem === null) return ''
  if (problem === 'ic-dph-mismatch') {
    return tt('IČ DPH neodpovedá DIČ - jedno z tých dvoch je preklep.')
  }
  if (field === 'ico') return tt('IČO má osem číslic a kontrolnú číslicu, ktorá tu nesedí.')
  if (field === 'dic') return tt('DIČ má desať číslic.')
  if (field === 'iban') return tt('IBAN nemá platnú kontrolnú sumu.')
  return tt('IČ DPH má tvar SK a desať číslic.')
}

export function identityLabel(field: IdentityField): string {
  if (field === 'ico') return tt('IČO')
  if (field === 'dic') return tt('DIČ')
  if (field === 'iban') return tt('IBAN')
  return tt('IČ DPH')
}

export interface BillingProblems {
  fields: { field: IdentityField; problem: IdentityProblem }[]
  /** Whether anything is wrong enough to stop a save. */
  blocking: boolean
}

export function problemsOf(form: BillingForm): BillingProblems {
  const fields: { field: IdentityField; problem: IdentityProblem }[] = []
  for (const field of ['ico', 'dic', 'iban'] as const) {
    const problem = identityProblem(field, form[field])
    if (problem !== null) fields.push({ field, problem })
  }
  const icDph = icDphProblem(form)
  if (icDph !== null) fields.push({ field: 'icDph', problem: icDph })
  return { fields, blocking: fields.length > 0 }
}

/**
 * Which of the four identifiers is still missing.
 *
 * On the restored data all four are empty strings, so every invoice this restaurant issues carries no
 * IČO, no DIČ and no bank account. That is a document a customer cannot pay from and an accountant will
 * send back — worth counting on the page rather than leaving as four blank boxes.
 */
export function missingIdentity(form: BillingForm): IdentityField[] {
  return (['ico', 'dic', 'icDph', 'iban'] as const).filter((field) => form[field].trim() === '')
}

// `footerLogoWouldChange` was here and is deleted — §9.35, 2026-08-06. It answered *is the typed logo
// different from the saved one*, so a screen could warn that changing it also changes the customer
// site's footer.
//
// The screen says that already, and unconditionally: the field is labelled *Logo na faktúre a v
// pätičke webu* and carries *Toto logo používa aj pätička zákazníckeho webu* underneath. A standing
// sentence that is true before anybody types is stronger than a warning that appears once they have —
// it is read while deciding rather than while committing.
